import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ActionableRecommendations = ({ analytics }) => {
  const [selectedPriority, setSelectedPriority] = useState('all');

  const recommendations = [
    {
      id: 1,
      title: 'Implement AI-Powered Chatbot',
      description: 'Deploy conversational AI to handle 70% of customer inquiries, reducing support costs by $15K/month',
      priority: 'high',
      impact: 'High ROI',
      effort: '2-3 weeks',
      category: 'Customer Experience',
      metrics: ['Response Time: -60%', 'Support Costs: -40%', 'CSAT: +25%'],
      steps: [
        'Select AI chatbot platform',
        'Train model on existing support data',
        'Integrate with current systems',
        'Launch beta and gather feedback'
      ]
    },
    {
      id: 2,
      title: 'Optimize Conversion Funnel',
      description: 'Reduce friction in sign-up process to increase conversion rate from 12.8% to 18%',
      priority: 'high',
      impact: 'Revenue Growth',
      effort: '1-2 weeks',
      category: 'Growth',
      metrics: ['Conversion Rate: +40%', 'Sign-ups: +2,500/month', 'Revenue: +$37K/month'],
      steps: [
        'A/B test simplified sign-up flow',
        'Remove unnecessary form fields',
        'Add social login options',
        'Implement progress indicators'
      ]
    },
    {
      id: 3,
      title: 'Launch Referral Program',
      description: 'Incentivize existing users to refer new customers with rewards program',
      priority: 'medium',
      impact: 'User Acquisition',
      effort: '3-4 weeks',
      category: 'Growth',
      metrics: ['CAC: -30%', 'Viral Coefficient: +0.4', 'New Users: +1,200/month'],
      steps: [
        'Design reward structure',
        'Build referral tracking system',
        'Create marketing materials',
        'Launch and promote to users'
      ]
    },
    {
      id: 4,
      title: 'Enhance Mobile Experience',
      description: 'Optimize mobile app performance to reduce bounce rate and increase engagement',
      priority: 'medium',
      impact: 'User Retention',
      effort: '4-6 weeks',
      category: 'Product',
      metrics: ['Mobile Bounce: -25%', 'Session Duration: +35%', 'Retention: +12%'],
      steps: [
        'Audit current mobile performance',
        'Implement lazy loading',
        'Optimize images and assets',
        'Add offline functionality'
      ]
    },
    {
      id: 5,
      title: 'Expand Content Marketing',
      description: 'Increase organic traffic through SEO-optimized blog content and thought leadership',
      priority: 'low',
      impact: 'Brand Awareness',
      effort: 'Ongoing',
      category: 'Marketing',
      metrics: ['Organic Traffic: +80%', 'Domain Authority: +15', 'Leads: +500/month'],
      steps: [
        'Develop content calendar',
        'Hire content writers',
        'Optimize for SEO',
        'Promote on social channels'
      ]
    },
    {
      id: 6,
      title: 'Implement Usage Analytics',
      description: 'Deploy comprehensive analytics to understand user behavior and product usage patterns',
      priority: 'high',
      impact: 'Data-Driven Decisions',
      effort: '2 weeks',
      category: 'Product',
      metrics: ['Feature Adoption: Visible', 'Churn Prediction: Enabled', 'Product Insights: +100%'],
      steps: [
        'Select analytics platform',
        'Instrument key events',
        'Create dashboards',
        'Train team on insights'
      ]
    }
  ];

  const priorities = [
    { value: 'all', label: 'All Priorities', color: 'text-foreground' },
    { value: 'high', label: 'High Priority', color: 'text-error' },
    { value: 'medium', label: 'Medium Priority', color: 'text-warning' },
    { value: 'low', label: 'Low Priority', color: 'text-primary' }
  ];

  const filteredRecommendations = selectedPriority === 'all'
    ? recommendations
    : recommendations?.filter(r => r?.priority === selectedPriority);

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'border-error bg-error/5',
      medium: 'border-warning bg-warning/5',
      low: 'border-primary bg-primary/5'
    };
    return colors?.[priority] || colors?.low;
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      high: 'bg-error text-white',
      medium: 'bg-warning text-white',
      low: 'bg-primary text-white'
    };
    return colors?.[priority] || colors?.low;
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-error/5 border border-error/20 rounded-xl p-4">
          <div className="text-2xl font-bold text-error mb-1">
            {recommendations?.filter(r => r?.priority === 'high')?.length}
          </div>
          <div className="text-sm text-muted-foreground">High Priority</div>
        </div>
        <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
          <div className="text-2xl font-bold text-warning mb-1">
            {recommendations?.filter(r => r?.priority === 'medium')?.length}
          </div>
          <div className="text-sm text-muted-foreground">Medium Priority</div>
        </div>
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <div className="text-2xl font-bold text-primary mb-1">
            {recommendations?.filter(r => r?.priority === 'low')?.length}
          </div>
          <div className="text-sm text-muted-foreground">Low Priority</div>
        </div>
        <div className="bg-success/5 border border-success/20 rounded-xl p-4">
          <div className="text-2xl font-bold text-success mb-1">
            {recommendations?.length}
          </div>
          <div className="text-sm text-muted-foreground">Total Recommendations</div>
        </div>
      </div>

      {/* Priority Filter */}
      <div className="flex flex-wrap gap-2">
        {priorities?.map((priority) => (
          <button
            key={priority?.value}
            onClick={() => setSelectedPriority(priority?.value)}
            className={`
              px-4 py-2 rounded-lg font-medium transition-all duration-250
              ${selectedPriority === priority?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }
            `}
          >
            {priority?.label}
          </button>
        ))}
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {filteredRecommendations?.map((rec) => (
          <div key={rec?.id} className={`border-2 rounded-xl p-6 ${getPriorityColor(rec?.priority)}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold text-foreground">{rec?.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityBadgeColor(rec?.priority)}`}>
                    {rec?.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{rec?.description}</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="Target" size={14} />
                    {rec?.impact}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    {rec?.effort}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="Tag" size={14} />
                    {rec?.category}
                  </span>
                </div>
              </div>
            </div>

            {/* Expected Metrics */}
            <div className="bg-background rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="TrendingUp" size={16} />
                Expected Impact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {rec?.metrics?.map((metric, index) => (
                  <div key={index} className="text-sm">
                    <span className="text-success font-semibold">{metric}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Implementation Steps */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="CheckSquare" size={16} />
                Implementation Steps
              </h4>
              <div className="space-y-2">
                {rec?.steps?.map((step, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      {index + 1}
                    </div>
                    <span className="text-sm text-foreground pt-0.5">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActionableRecommendations;